import { toLocalISOString } from "./dateAndTimeUtils";

/**
 * Calculate time difference between two points and format the result as a string with minutes and seconds.
 * @param {Object} prevPoint - The previous point with a known pass time.
 * @param {Object} currPoint - The current point where time to the next point is calculated.
 * @returns {string} Time difference formatted as "Xm Ys".
 * 
 * @category Utils
 */
function getTimeToInMins(prevPoint, currPoint) {
    let timeTo;
    const previousTime = new Date(prevPoint.Est_PassTime || prevPoint.PassTime);
    const currentTime = new Date(currPoint.Est_PassTime || currPoint.PassTime);
    if (previousTime && currentTime) {
        const timeDifference = (currentTime.getTime() - previousTime.getTime()) / 1000; // Get time in seconds
        const minutes = Math.floor(timeDifference / 60); // Get whole minutes
        const seconds = Math.floor(timeDifference % 60); // Get remaining seconds
        timeTo = `${minutes}m ${seconds}s`; // Format in "Xm Ys"
    }
    return timeTo;
}

/**
 * Calculate estimated pass time based on speed and distance from the previous point.
 * @param {Object} currPoint - Current point for which to calculate the estimated pass time.
 * @param {Object} prevPoint - Previous point with a known pass time.
 * @param {number} speed - Speed in km/h to be used for calculation.
 * @returns {string} The estimated pass time as an ISO string.
 * 
 * @category Utils
 */
const getPassTime = (currPoint, prevPoint, speed) => {
    const initialSpeedKmH = speed; //
    const initialSpeedMs = initialSpeedKmH * (1000 / 3600); // Convert speed from km/h to m/s
    const timePerMeter = 1 / initialSpeedMs;
    const distance =  currPoint.AGMs[0].Real_Chain - prevPoint.AGMs[0].Real_Chain;

    // Calculate travel time in seconds from the previous point to the current point
    const travelTime = (distance * 1000) * timePerMeter;
    let previousPassTime;
    if(!prevPoint.Est_PassTime){
        previousPassTime = new Date(prevPoint.PassTime);
    } else {
        previousPassTime = new Date(prevPoint.Est_PassTime);
    }

    const newPassTime = new Date(previousPassTime.getTime() + travelTime * 1000);

    const formattedPassTime = toLocalISOString(newPassTime);

    return formattedPassTime;
}

/**
 * Calculate and update estimated pass times and speeds for an array of geographic points.
 * @param {Array} agmPoints - Array of points with geographic and timing data.
 * @returns {Array} The updated array of points with recalculated pass times and speeds.
 *
 * @category Utils
 */
// Helper functions to get values from either top level or AGMs[0]
// Use explicit null/undefined checks (not truthy) to handle Speed=0 and empty string PassTime
const getPassTimeValue = (p) => p.PassTime ?? p.AGMs?.[0]?.PassTime;
const getSpeedValue = (p) => p.Speed ?? p.AGMs?.[0]?.Speed;
const hasPassTimeValue = (p) => getPassTimeValue(p) !== null && getPassTimeValue(p) !== undefined;
const hasSpeedValue = (p) => getSpeedValue(p) !== null && getSpeedValue(p) !== undefined;

function estimatePassTimes (agmPoints) {
    if (!agmPoints.length) return [];

    // Use explicit null/undefined checks for start-point selection (not truthy)
    let previousPoint = agmPoints.find(p => hasPassTimeValue(p) && hasSpeedValue(p));
    if (!previousPoint) return agmPoints; // No starting point with time and speed

    // Normalize: ensure PassTime and Speed are at top level for the starting point
    if (previousPoint.PassTime === undefined || previousPoint.PassTime === null) {
        previousPoint.PassTime = previousPoint.AGMs?.[0]?.PassTime;
    }
    if (previousPoint.Speed === undefined || previousPoint.Speed === null) {
        previousPoint.Speed = previousPoint.AGMs?.[0]?.Speed;
    }

    //use array to track what indices need to be updated
    //these are the points behind the point where a passtime is found
    let updateIndices = [];

    // Iterate through the array starting from the point after the initial reference point
    for (let i = agmPoints.indexOf(previousPoint) + 1; i < agmPoints.length; i++) {
        const currentPoint = agmPoints[i];

        // Normalize currentPoint PassTime if it's in AGMs[0]
        const currentPassTime = getPassTimeValue(currentPoint);
        if (currentPassTime !== null && currentPassTime !== undefined && (currentPoint.PassTime === null || currentPoint.PassTime === undefined)) {
            currentPoint.PassTime = currentPassTime;
        }

        // Normalize currentPoint Speed if it's only in AGMs[0]
        const currentSpeed = getSpeedValue(currentPoint);
        if (currentSpeed !== null && currentSpeed !== undefined && (currentPoint.Speed === null || currentPoint.Speed === undefined)) {
            currentPoint.Speed = currentSpeed;
        }

        // Update previous point reference if current has its own pass time and speed
        if (currentPoint.PassTime && currentPoint.Approved) { // && currentPoint.Approved
            //use the real chain to calculate distance
            const distanceKm = currentPoint.AGMs[0].Real_Chain - previousPoint.AGMs[0].Real_Chain;

            const timeDiff = (new Date(currentPoint.PassTime).getTime() - new Date(previousPoint.PassTime).getTime()) / 3600000; // Convert time difference from ms to hours

            // Calculate actual speed in km/h
            if (timeDiff > 0) { // Prevent division by zero
                const actualSpeedKmH = distanceKm / timeDiff;

                currentPoint.Speed = actualSpeedKmH.toFixed(3); // Set the newly calculated speed
                let previousSpeed = previousPoint.Speed;
                //change the speed/estimated passtimes/time to of previous points

                //CHECK WITH RAY TO SEE IF WE UPDATE THE PREVIOUS ESTIMATED TIMES AND TIME TOS BASED
                //ON THE NEW SPEED!!!!!!!!!!!!!

                // updateIndices.forEach(index => {
                //     // agmPoints[index].Speed = actualSpeedKmH.toFixed(3); //**THIS IS FOR SETTING PREVIOUS POINTS TO SPEED OF ELEMENT AHEAD */
                //     agmPoints[index].Speed = previousSpeed;//THIS IS FOR SETTING PREBOUS POINTS TO SPEED FROM BEHIND
                //     let passTime = getPassTime(agmPoints[index], agmPoints[index-1], actualSpeedKmH);
                //     agmPoints[index].Est_PassTime = passTime;
                //     let timeTo = getTimeToInMins(agmPoints[index-1], agmPoints[index]);
                //     agmPoints[index].timeTo = timeTo;
                // });
                updateIndices = [];
            }

            if(currentPoint.Est_PassTime) delete currentPoint.Est_PassTime;

            currentPoint.timeTo = getTimeToInMins(agmPoints[i-1], currentPoint);

            previousPoint = currentPoint; // Update the reference to the current point
            continue; // Move to the next iteration
        }

        // Continue using the same speed if the current point doesn't have its own
        const initialSpeedKmH = previousPoint.Speed;
        const initialSpeedMs = initialSpeedKmH * (1000 / 3600); // Convert speed from km/h to m/s
        const timePerMeter = 1 / initialSpeedMs;

        //use real_chain to get distance - this takes in to account curvature and entered real_chain distances
        //HAVE TO EXPECT REAL_CHAIN TO ALWAYS BE THERE
        const distance =  currentPoint.AGMs[0].Real_Chain - previousPoint.AGMs[0].Real_Chain;

        // Calculate travel time in seconds from the previous point to the current point
        const travelTime = (distance * 1000) * timePerMeter;
        const previousPassTime = new Date(previousPoint.PassTime);

        // Check if previousPassTime is valid
        if (isNaN(previousPassTime.getTime())) {
            continue;  // Skip this iteration if the time is invalid
        }

        const newPassTime = new Date(previousPassTime.getTime() + travelTime * 1000);

        // Check if newPassTime is valid
        if (isNaN(newPassTime.getTime())) {
            continue;  // Skip this iteration if the time is invalid
        }

        currentPoint.Est_PassTime = newPassTime.toISOString();
        currentPoint.Speed = previousPoint.Speed;

        currentPoint.timeTo = getTimeToInMins(agmPoints[i-1], currentPoint);

        updateIndices.push(i);
    }

    return agmPoints;
}

export default estimatePassTimes;